import { useCrmDashboard, useDeals, useAppointments } from '@/hooks/useCRM';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, DollarSign, CalendarDays, MessageSquare, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import type { DealStage } from '@/types/models';

const STAGE_COLORS: Record<DealStage, string> = {
  lead: 'bg-muted text-muted-foreground',
  qualified: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  proposal: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  negotiation: 'bg-purple-500/15 text-purple-700 dark:text-purple-300',
  won: 'bg-green-500/15 text-green-700 dark:text-green-300',
  lost: 'bg-destructive/15 text-destructive',
};

export default function CrmDashboardPage() {
  const { data: dashboard } = useCrmDashboard();
  const { data: deals } = useDeals();
  const { data: appointments } = useAppointments();

  const upcomingAppts = (appointments ?? [])
    .filter(a => a.status === 'scheduled')
    .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
    .slice(0, 5);

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="CRM Dashboard" subtitle="Pipeline overview, contacts, and upcoming activity" />

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Contacts</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{dashboard?.total_contacts ?? 0}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Open Deals</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{dashboard?.open_deals ?? 0}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pipeline Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">${(dashboard?.pipeline_value ?? 0).toLocaleString()}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Upcoming Appts</CardTitle>
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{dashboard?.upcoming_appointments ?? 0}</div></CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pipeline by Stage */}
        <Card>
          <CardHeader><CardTitle>Pipeline by Stage</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard?.deals_by_stage && Object.entries(dashboard.deals_by_stage).map(([stage, count]) => (
                <div key={stage} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={STAGE_COLORS[stage as DealStage]}>{stage}</Badge>
                  </div>
                  <span className="text-sm font-semibold">{count as number}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Appointments */}
        <Card>
          <CardHeader><CardTitle>Upcoming Appointments</CardTitle></CardHeader>
          <CardContent>
            {upcomingAppts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming appointments</p>
            ) : (
              <div className="space-y-3">
                {upcomingAppts.map(apt => (
                  <div key={apt.id} className="flex items-start justify-between rounded-md border border-border p-3">
                    <div>
                      <p className="text-sm font-medium">{apt.title}</p>
                      <p className="text-xs text-muted-foreground">{apt.contact_name} — {apt.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium">{format(new Date(apt.start_at), 'MMM d')}</p>
                      <p className="text-xs text-muted-foreground">{format(new Date(apt.start_at), 'h:mm a')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Communications */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Recent Activity</CardTitle></CardHeader>
        <CardContent>
          {(dashboard?.recent_communications ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No recent communications</p>
          ) : (
            <div className="space-y-3">
              {(dashboard?.recent_communications ?? []).map((comm: any) => (
                <div key={comm.id} className="flex items-start gap-3 border-b border-border pb-3 last:border-0">
                  <Badge variant="outline" className="shrink-0">{comm.channel}</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{comm.subject || comm.body?.slice(0, 80)}</p>
                    <p className="text-xs text-muted-foreground">{comm.logged_by_name} — {format(new Date(comm.occurred_at), 'MMM d, h:mm a')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
